/**
 * Client-side offer attribution.
 *
 * When a visitor starts an approval from a specific live rate offer we stash
 * the same non-sensitive `offerEventMeta` dimensions used for analytics, so the
 * lead form, the confirmation page, and ultimately the CRM record all show
 * which offer (and which path — card vs Simply AI) produced the lead.
 *
 * Never contains borrower identity or wholesale investor identity.
 */

export type OfferAttribution = Record<string, string | number | boolean> & {
  attributed_at?: string;
};

const KEY = "sam.offerAttribution";
/** Attribution goes stale with pricing. */
const MAX_AGE_MS = 6 * 60 * 60 * 1000;

export function storeOfferAttribution(
  meta: Record<string, string | number | boolean | null | undefined>,
): void {
  if (typeof window === "undefined") return;
  const clean: OfferAttribution = { attributed_at: new Date().toISOString() };
  for (const [k, v] of Object.entries(meta)) {
    if (v === null || v === undefined || v === "") continue;
    clean[k] = v;
  }
  try {
    window.sessionStorage.setItem(KEY, JSON.stringify(clean));
  } catch {
    // storage disabled — the lead still submits, just without offer attribution
  }
}

export function loadOfferAttribution(): OfferAttribution | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as OfferAttribution;
    const at = typeof parsed?.attributed_at === "string" ? Date.parse(parsed.attributed_at) : NaN;
    if (!Number.isFinite(at) || Date.now() - at > MAX_AGE_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Flattens the attribution into the lead's calculator snapshot with an
 * `offer_` prefix so staff (and the CRM) can read it at a glance.
 */
export function offerAttributionSnapshot(
  a: OfferAttribution | null,
): Record<string, string | number | boolean> {
  if (!a) return {};
  const out: Record<string, string | number | boolean> = {};
  for (const [k, v] of Object.entries(a)) {
    if (v === null || v === undefined || v === "") continue;
    out[k.startsWith("offer_") ? k : `offer_${k}`] = v as string | number | boolean;
  }
  return out;
}
